/**
 * <nia-buy-options>
 * Fusiona variantes y selling plans en una sola lista de opciones de compra.
 * Cada fila ya trae en atributos su precio, su comparativo y su ahorro, todos
 * formateados por Liquid: aquí solo se copian cadenas entre nodos, así que el
 * formato de moneda de la tienda nunca se reimplementa en JavaScript.
 *
 * Emite nia:buy-options:change con { variantId, sellingPlanId, price } para
 * que el botón de compra y la barra fija lo escuchen, y nia:variant:change
 * con el mediaId para que la galería salte a la imagen de la variante.
 */
const DONE_MS = 1600;

class NiaBuyOptions extends HTMLElement {
  #qty = 1;

  connectedCallback() {
    this.options = Array.from(this.querySelectorAll('[data-buy-option]'));
    this.priceEl = this.querySelector('[data-buy-price]');
    this.wasEl = this.querySelector('[data-buy-was]');
    this.saveEl = this.querySelector('[data-buy-save]');

    this.form = this.querySelector('#nia-product-form');
    this.variantInput = this.querySelector('[data-buy-variant-input]');
    this.planInput = this.querySelector('[data-buy-plan-input]');
    this.qtyInput = this.querySelector('[data-buy-qty-input]');
    this.qtyValue = this.querySelector('[data-buy-qty-value]');
    this.atc = this.querySelector('[data-buy-atc]');
    this.errorEl = this.querySelector('[data-buy-error]');
    this.statusEl = this.querySelector('[data-buy-status]');

    this.subs = Array.from(this.querySelectorAll('[data-buy-sub]'));
    this.unitEl = this.querySelector('[data-buy-unit]');
    this.unitGrams = Number(this.dataset.unitGrams) || 0;

    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.labels = {
      add: this.dataset.addLabel || 'Agregar al carrito',
      subscribe: this.dataset.subscribeLabel || 'Suscribirme',
    };

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.form?.addEventListener('submit', this.#onSubmit);

    this.#restoreFromUrl();
    this.#apply(this.#current, { updateUrl: false });
  }

  /** Formatea centavos con el money_format de la tienda. */
  #money(cents) {
    const amount = cents / 100;
    const withDecimals = amount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const noDecimals = Math.round(amount).toLocaleString('es-MX');

    return this.moneyFormat
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, withDecimals);
  }

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const step = target.closest('[data-buy-qty]');
    if (step instanceof HTMLButtonElement && !step.disabled) {
      this.#setQuantity(this.#qty + Number(step.dataset.buyQty));
      return;
    }

    const more = target.closest('[data-buy-text-toggle]');
    if (more instanceof HTMLElement) {
      const abierto = more.getAttribute('aria-expanded') === 'true';
      more.setAttribute('aria-expanded', String(!abierto));
      const texto = document.getElementById(more.getAttribute('aria-controls') ?? '');
      if (texto) texto.toggleAttribute('data-collapsed', abierto);
      return;
    }

    if (target.closest('[data-buy-notify-toggle]')) {
      const form = this.querySelector('#nia-notify-form');
      if (form) {
        form.hidden = !form.hidden;
        if (!form.hidden) form.querySelector('input[type="email"]')?.focus();
      }
    }
  };

  #setQuantity(next) {
    const option = this.#current;
    const tracked = option?.dataset.max === 'shopify' && option?.dataset.policy === 'deny';
    const ceiling = tracked ? Math.max(1, Number(option.dataset.inventory) || 1) : Infinity;

    this.#qty = Math.min(Math.max(1, next), ceiling);

    if (this.qtyValue) this.qtyValue.textContent = String(this.#qty);
    if (this.qtyInput) this.qtyInput.value = String(this.#qty);

    const minus = this.querySelector('[data-buy-qty="-1"]');
    const plus = this.querySelector('[data-buy-qty="1"]');
    if (minus) minus.disabled = this.#qty <= 1;
    if (plus) plus.disabled = this.#qty >= ceiling;

    this.#paintSubtitles();
    this.#paintPrices();
    this.#paintRowPrices();
    this.#paintButton(this.#current);
    this.#emit();
  }

  /**
   * Todo cambio —fila, plan o cantidad— pasa por #setQuantity, así que este es
   * el unico punto desde el que se avisa afuera. El detalle lleva ya la cadena
   * de precio compuesta, no centavos: quien escucha no vuelve a formatear.
   *
   * Se guarda ademas en lastChange porque el orden en que se activan los
   * componentes no esta garantizado. Si la barra fija se activa despues, el
   * primer aviso ya paso y lo recupera de ahi; si se activa antes, lo recibe
   * por el evento. Sin esto, una ficha abierta con ?selling_plan= en la URL
   * dejaria la barra anunciando el precio de la compra unica.
   */
  #emit() {
    const option = this.#current;
    if (!option) return;

    const d = option.dataset;
    const cents = Number(d.priceCents);
    const isSubscription = d.subscription === 'true';
    const input = option.querySelector('input');

    const detail = {
      variantId: d.variant ?? '',
      sellingPlanId: d.sellingPlan ?? '',
      quantity: this.#qty,
      price: Number.isFinite(cents)
        ? `${this.#money(cents * this.#qty)}${isSubscription ? this.dataset.periodSuffix || '/mes' : ''}`
        : '',
      subscription: isSubscription,
      optionName: option.querySelector('.nia-buy__name')?.textContent.trim() ?? '',
      available: !option.classList.contains('nia-buy__option--out') && !input?.disabled,
    };

    this.lastChange = detail;
    document.dispatchEvent(new CustomEvent('nia:buy-options:change', { detail }));
  }

  /**
   * Encabezado, comparativo y ahorro, todos por la cantidad. Se calculan desde
   * los centavos y no multiplicando la cadena ya formateada, que traería la
   * moneda y los separadores pegados.
   */
  #paintPrices() {
    const option = this.#current;
    if (!option) return;
    const d = option.dataset;
    const n = this.#qty;

    const cents = Number(d.priceCents);
    if (this.priceEl && Number.isFinite(cents)) this.priceEl.textContent = this.#money(cents * n);

    if (this.wasEl) {
      const wasCents = Number(d.wasCents);
      const hay = d.was !== '' && Number.isFinite(wasCents) && wasCents > 0;
      this.wasEl.textContent = hay ? this.#money(wasCents * n) : '';
      this.wasEl.hidden = !hay;
    }

    if (this.saveEl) {
      this.saveEl.textContent = this.#saveText(option);
      this.saveEl.hidden = this.saveEl.textContent === '';
    }

    // El gramaje del encabezado sigue a la cantidad: 4 latas son 120 g.
    if (this.unitEl && this.unitGrams && this.dataset.unitFormat) {
      const gramos = Math.round(n * this.unitGrams * 100) / 100;
      this.unitEl.textContent = this.dataset.unitFormat.replace('{g}', String(gramos));
    }
  }

  /**
   * Un ahorro en porcentaje no depende de la cantidad y se deja como está; uno
   * en dinero sí, y por eso viaja también en centavos.
   */
  #saveText(option) {
    const d = option.dataset;
    const saveCents = Number(d.saveCents);
    if (Number.isFinite(saveCents) && saveCents > 0) {
      return `${d.saveLabel ?? ''} ${this.#money(saveCents * this.#qty)}`.trim();
    }
    return d.save ?? '';
  }

  /** El precio de cada fila, para que cuadre con su propio descriptor. */
  #paintRowPrices() {
    this.options.forEach((option) => {
      const nodo = option.querySelector('[data-buy-row-price]');
      if (!nodo) return;
      if (option.classList.contains('nia-buy__option--out')) return;

      const cents = Number(option.dataset.priceCents);
      if (!Number.isFinite(cents)) return;
      nodo.textContent = `${this.#money(cents * this.#qty)}${nodo.dataset.suffix ?? ''}`;

      const badge = option.querySelector('[data-buy-row-save]');
      if (badge) badge.textContent = this.#saveText(option);
    });
  }

  /**
   * "2 latas (60 g)". La frase se arma con los moldes que vienen del Liquid,
   * así que ni la unidad ni el formato están escritos aquí. Cada descriptor
   * guarda su propio molde en data-buy-sub y solo se le sustituye {q}: eso
   * deja intacta la cola de cada fila, que en la de suscripción es distinta.
   */
  #quantityPhrase() {
    const n = this.#qty;
    const unidad = n === 1 ? this.dataset.unitOne : this.dataset.unitMany;
    if (!this.unitGrams) return `${n} ${unidad ?? ''}`.trim();

    // Los gramos se redondean: 3 x 2.5 g no debe salir como 7.500000000000001.
    const gramos = Math.round(n * this.unitGrams * 100) / 100;
    return (this.dataset.qtyFormat || '{n} {u} ({g} g)')
      .replace('{n}', String(n))
      .replace('{u}', unidad ?? '')
      .replace('{g}', String(gramos));
  }

  #paintSubtitles() {
    if (this.subs.length === 0) return;
    const frase = this.#quantityPhrase();
    this.subs.forEach((nodo) => {
      const molde = nodo.dataset.buySub;
      if (molde) nodo.textContent = molde.replace('{q}', frase);
    });
  }

  /** El precio del botón es el de la opción por la cantidad. */
  #paintButton(option) {
    if (!this.atc || !option) return;
    const unit = Number(option.dataset.priceCents);
    if (!Number.isFinite(unit)) return;

    const isSubscription = option.dataset.subscription === 'true';
    const label = isSubscription ? this.labels.subscribe : this.labels.add;
    const suffix = isSubscription ? this.dataset.periodSuffix || '/mes' : '';

    this.atc.textContent = `${label} — ${this.#money(unit * this.#qty)}${suffix}`;
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    if (!this.atc || !this.form) return;

    const original = this.atc.textContent;
    this.atc.setAttribute('busy', '');
    this.atc.disabled = true;
    this.#setError('');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || `cart/add.js respondió ${response.status}`);
      }

      this.atc.removeAttribute('busy');
      this.atc.textContent = 'Agregado ✓';
      if (this.statusEl) this.statusEl.textContent = 'Producto agregado al carrito';
      document.getElementById('nia-cart-drawer')?.openWithRefresh?.();

      setTimeout(() => {
        this.atc.textContent = original;
        this.atc.disabled = false;
        if (this.statusEl) this.statusEl.textContent = '';
      }, DONE_MS);
    } catch (error) {
      this.atc.removeAttribute('busy');
      this.atc.textContent = original;
      this.atc.disabled = false;
      this.#setError(error.message || 'No se pudo agregar. Inténtalo de nuevo.');
    }
  };

  #setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = message === '';
  }

  get #current() {
    return this.options.find((option) => option.querySelector('input')?.checked) ?? this.options[0];
  }

  /** Si la URL trae variante y plan, esa es la fila que arranca marcada. */
  #restoreFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const variant = params.get('variant');
    if (!variant) return;

    const plan = params.get('selling_plan') ?? '';
    const match = this.options.find(
      (option) => option.dataset.variant === variant && (option.dataset.sellingPlan || '') === plan
    );
    const input = match?.querySelector('input');
    if (input && !input.disabled) input.checked = true;
  }

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-buy-freq]')) {
      this.#onFrequencyChange(target);
      return;
    }

    if (target.matches('input[type="radio"]')) this.#apply(this.#current);
  };

  /** Cambiar la frecuencia cambia el plan y el precio de la misma fila. */
  #onFrequencyChange(select) {
    const option = select.closest('[data-buy-note]')?.previousElementSibling;
    if (!option?.matches('[data-buy-option]')) return;

    const chosen = select.options[select.selectedIndex];
    option.dataset.sellingPlan = select.value;
    option.dataset.price = chosen.dataset.price ?? option.dataset.price;

    const priceEl = option.querySelector('.nia-buy__option-price');
    if (priceEl) priceEl.textContent = `${chosen.dataset.price}${chosen.dataset.priceSuffix ?? ''}`;

    this.#apply(option);
  }

  /**
   * @param {HTMLElement} option
   * @param {{updateUrl?: boolean}} [config]
   */
  #apply(option, config = {}) {
    if (!option) return;

    const { price = '', variant, sellingPlan = '', media } = option.dataset;
    const isSubscription = option.dataset.subscription === 'true';

    // Los precios los pinta #setQuantity, que es quien conoce la cantidad.

    // La nota solo se ve bajo la fila de suscripción que está elegida.
    this.querySelectorAll('[data-buy-note]').forEach((note) => {
      note.hidden = !(isSubscription && note.previousElementSibling === option);
    });

    // El formulario real es el que se envía, con o sin JavaScript: hay que
    // mantener sus campos ocultos sincronizados con la fila elegida.
    if (this.variantInput) this.variantInput.value = variant ?? '';
    if (this.planInput) {
      this.planInput.value = sellingPlan;
      this.planInput.disabled = sellingPlan === '';
    }

    this.#setQuantity(this.#qty);

    if (config.updateUrl !== false) this.#updateUrl(variant, sellingPlan);

    if (media) {
      document.dispatchEvent(new CustomEvent('nia:variant:change', { detail: { mediaId: media } }));
    }
  }

  /**
   * replaceState y no pushState: a diferencia de los filtros de la tienda,
   * aquí cada clic es un ajuste de la misma decisión. Con pushState, salir de
   * la ficha exigiría deshacer una por una todas las opciones probadas.
   */
  #updateUrl(variantId, sellingPlanId) {
    const url = new URL(window.location.href);
    if (variantId) url.searchParams.set('variant', variantId);
    else url.searchParams.delete('variant');

    if (sellingPlanId) url.searchParams.set('selling_plan', sellingPlanId);
    else url.searchParams.delete('selling_plan');

    history.replaceState({}, '', url.toString());
  }
}

if (!customElements.get('nia-buy-options')) {
  customElements.define('nia-buy-options', NiaBuyOptions);
}
